// React helpers over utils/telemetry.js.
//
// `usePanelDwell('panel.sites')` returns a ref to put on a panel's root: the
// panel then reports how long it was actually on screen. Visibility comes from
// an IntersectionObserver, so a panel scrolled out of view stops counting, and
// telemetry.js additionally stops the clock on a hidden tab or an idle user.
import { useCallback, useEffect, useRef } from 'react';

import { setVisible, setContext, setFilters, trackFilterDiff } from './telemetry';

const VISIBLE_RATIO = 0.25;   // a quarter on screen counts as being looked at

/** Ref callback that reports this element's on-screen time under `target`. */
export function usePanelDwell(target) {
  const observer = useRef(null);
  const current = useRef(null);

  useEffect(() => () => {
    if (observer.current) observer.current.disconnect();
    if (current.current) setVisible(target, false);
  }, [target]);

  return useCallback((node) => {
    if (observer.current) {
      observer.current.disconnect();
      observer.current = null;
    }
    if (current.current && current.current !== node) {
      setVisible(target, false);
    }
    current.current = node;
    if (!node || !target || typeof IntersectionObserver === 'undefined') return;
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setVisible(target, entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO);
      });
    }, { threshold: [0, VISIBLE_RATIO, 1] });
    observer.current.observe(node);
  }, [target]);
}

/** Keep the telemetry context in step with the dashboard's mode and source. */
export function useTelemetryContext(mode, source) {
  useEffect(() => {
    setContext({ mode: mode || null, source: source || null });
  }, [mode, source]);
}

/**
 * Register a flat snapshot of the dashboard's settings.
 *
 * Two things happen per change: one `filter` event per key that moved (so
 * "which controls do they touch" is answerable), and a filter-set hash stamped
 * on every later event (so "what was set at the time" is answerable). The full
 * set is uploaded once per distinct combination, never per event.
 *
 * Debounced, so dragging a slider is one event and not forty.
 */
export function useTrackedSettings(snapshot, delay = 800) {
  const prev = useRef(null);
  const timer = useRef(null);
  const json = JSON.stringify(snapshot || {});

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const next = JSON.parse(json);
      trackFilterDiff(prev.current, next);
      setFilters(next);
      prev.current = next;
    }, prev.current === null ? 0 : delay);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [json, delay]);
}
